import { z } from 'zod';
import type { CreatePurchaseDocumentInput, UpdatePurchaseDocumentInput } from '../../../interfaces/src';
import type { TypedSupabaseClient } from '../client';
import { buildPurchaseReceptionStockEffects } from '@erp/domain/purchases';

const request = z.string().uuid();
const finite = z.number().finite();
const documentSchema = z.object({
  docType: z.enum(['order','reception','invoice','credit_note']), number: z.string().trim().min(1).max(50),
  supplierId: request, docDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  lines: z.array(z.object({description:z.string().min(1).max(500),quantity:finite.positive(),unitPriceHt:finite.nonnegative(),vatRate:finite.nonnegative(),
    discountPct:finite.min(0).max(100).optional(),totalHt:finite.optional(),totalTtc:finite.optional(),articleId:request.optional(),variantId:request.nullable().optional(),variantLabel:z.string().nullable().optional(),
  }).passthrough()).min(1),
}).passthrough();
export async function resumePurchaseDraftMutation(client: TypedSupabaseClient, id: string | null, command: CreatePurchaseDocumentInput | UpdatePurchaseDocumentInput): Promise<string | null> {
  const { data, error } = await client.rpc('resume_purchase_document_draft_atomic' as never, {
    p_request_id: request.parse(command.clientRequestId), p_document_id: id, p_command: command,
  } as never);
  if (error) throw error;
  return data === null ? null : request.parse(data);
}
export async function savePurchaseDraftMutation(client: TypedSupabaseClient, id: string | null, command: CreatePurchaseDocumentInput | UpdatePurchaseDocumentInput, full: CreatePurchaseDocumentInput): Promise<string> {
  documentSchema.parse(full);
  const lines = full.lines.map((line,index)=>({line_number:index+1, article_id:line.articleId??null, variant_id:line.variantId??null, variant_label:line.variantLabel??null,
    description:line.description, quantity:line.quantity, unit:line.unit??null, unit_price_ht:line.unitPriceHt,
    purchase_price:line.unitPriceHt, discount_pct:line.discountPct??0, vat_rate:line.vatRate,
    total_ht:line.totalHt??0,total_ttc:line.totalTtc??0,source_doc_id:line.sourceDocId??null}));
  const document = {doc_type:full.docType,number:full.number,supplier_id:full.supplierId,warehouse_id:full.warehouseId??null,
    doc_date:full.docDate,parent_doc_id:full.parentDocId??null,parent_doc_ids:full.parentDocIds??(full.parentDocId?[full.parentDocId]:[]),
    notes:full.notes??null,custom_properties:full.customProperties??{},vat_exempt:full.vatExempt??false,
    total_ht:full.totalHt??0,total_vat:full.totalVat??0,total_ttc:full.totalTtc??0,
    withholding_amount:full.withholdingAmount??0,stamp_amount:full.stampAmount??0,net_to_pay:full.netToPay??0};
  const stockEffects = full.docType==='reception' ? buildPurchaseReceptionStockEffects(full.lines).map(effect=>({article_id:effect.articleId,quantity:effect.quantity})) : [];
  const {data,error}=await client.rpc('save_purchase_document_draft_atomic' as never, {
    p_request_id:request.parse(command.clientRequestId),p_document_id:id,p_command:command,p_document:document,p_lines:lines,
    p_stock_effects:stockEffects,p_base_updated_at:'baseUpdatedAt' in command ? command.baseUpdatedAt??null : null,
  } as never);
  if(error)throw error;
  return request.parse(data);
}
